import React, { useState } from 'react';
import { ActivityIndicator, FlatList, View } from 'react-native';

import { Text, TextInput } from '../../components';
import { PrimaryButton, Sheet, TickRow } from '../../design/components';
import usePagedSearchQuery from '../../hooks/usePagedSearchQuery';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const PickerBody = ({ title, items = [], allItems = items, selected = [], multiple, allLabel, onChange, onClose, search, setSearch, loading, error, loadMore, retry }) => {
	const styles = useThemedStyles(createStyles);
	const [draft, setDraft] = useState(selected);
	const [all, setAll] = useState(selected === null);
	const toggle = item => {
		if (!multiple) {
			onChange(item);
			onClose();
			return;
		}
		const ids = all ? allItems.map(option => option.id) : draft;
		setAll(false);
		setDraft(ids.includes(item.id) ? ids.filter(id => id !== item.id) : [...ids, item.id]);
	};

	return (
		<Sheet
			isVisible={true}
			onClose={onClose}
			title={title}
			fullHeight={true}
			scroll={false}
			footer={multiple ? <PrimaryButton label="Done" onPress={() => { onChange(all ? null : draft); onClose(); }} /> : null}
		>
			<TextInput value={search} onChange={setSearch} placeholder="Search" accessibilityLabel={`Search ${title.toLowerCase()}`} autoCorrect={false} />
			{Boolean(allLabel) && <TickRow title={allLabel} checked={all} quietWhenChecked={false} onToggle={() => { setAll(!all); setDraft([]); }} />}
			<FlatList
				style={styles.list}
				contentContainerStyle={styles.rows}
				data={items}
				keyExtractor={item => item.id}
				keyboardShouldPersistTaps="handled"
				onEndReached={loadMore}
				onEndReachedThreshold={0.5}
				renderItem={({ item }) => <TickRow title={item.name || 'Unnamed option'} checked={all || draft?.includes(item.id)} quietWhenChecked={false} onToggle={() => toggle(item)} />}
				ListEmptyComponent={!loading && !error ? <Text>No matching options.</Text> : null}
				ListFooterComponent={(
					<View>
						{loading && <ActivityIndicator />}
						{Boolean(error) && <PrimaryButton label="Could not load options. Try again" onPress={retry} />}
					</View>
				)}
			/>
		</Sheet>
	);
};

const LocalPicker = ({ items, ...props }) => {
	const [search, setSearch] = useState('');
	const filtered = items.filter(item => (item.name || '').toLowerCase().includes(search.trim().toLowerCase()));
	// Keep the full set for "all minus one", even while the visible list is searched.
	return <PickerBody {...props} items={filtered} allItems={items} search={search} setSearch={setSearch} />;
};

const RemotePicker = ({ document, field, filter, getName, ...props }) => {
	const { items, search, setSearch, loading, error, loadMore, refetch } = usePagedSearchQuery({ document, field, filter, isActive: true });
	return <PickerBody {...props} items={items?.map(item => ({ ...item, name: getName ? getName(item) : item.name }))} search={search} setSearch={setSearch} loading={loading} error={error} loadMore={loadMore} retry={refetch} />;
};

const ServiceRunPlanningPicker = props => (props.document ? <RemotePicker {...props} /> : <LocalPicker {...props} />);

const createStyles = ({ ui: { space } }) => ({
	list: { flex: 1 },
	rows: { gap: space.sm, paddingBottom: space.sm },
});

export default ServiceRunPlanningPicker;
